import { OrbitControls, PerspectiveCamera } from '@react-three/drei'
import { useFrame } from '@react-three/fiber'
import { useAssetVizInternal } from '../AssetVizInternalProvider'
import { MouseHandler } from '../systems/MouseHandler'
import { getBoundingBoxOfAssetModel } from '../utils/multiComponentUtils'
import { RenderLayerID } from '../utils/renderingDefines'
import React, { useState } from 'react'
import * as THREE from 'three'
import { OrbitControls as OrbitControlsImpl } from 'three-stdlib'
import { GeckoAssetModel } from 'types'

const cameraFitMultiplier = 3
const subVector = new THREE.Vector3()
const cameraVector = new THREE.Vector3()

const calculateCameraDistance = (
  boundingBoxSize: THREE.Vector3,
  fov: number,
  aspect: number
): number => {
  const verticalFOV = THREE.MathUtils.degToRad(fov)
  const horizontalFOV = aspect * verticalFOV

  const maxDimension = Math.max(
    boundingBoxSize.x,
    boundingBoxSize.y,
    boundingBoxSize.z
  )

  const distToFitHorizontal =
    (0.5 * maxDimension) / Math.tan(0.5 * horizontalFOV)
  const distToFitVertical = (0.5 * maxDimension) / Math.tan(0.5 * verticalFOV)

  return Math.max(distToFitHorizontal, distToFitVertical)
}

export const MultiComponentCamera: React.FC<{
  assetModel: GeckoAssetModel
  autoRecenter?: boolean
}> = ({ assetModel, autoRecenter = false }) => {
  //----------------------------------------------------------------------------
  const { setController } = useAssetVizInternal()

  const { show3D } = useAssetVizInternal()

  const [cameraShow3D, setCameraShow3D] = useState<boolean>(!show3D)

  const cameraRef = React.useRef<THREE.PerspectiveCamera>(null)
  const controlsRef = React.useRef<OrbitControlsImpl>(null)
  const isUserInteracting = React.useRef<boolean>(false)

  React.useEffect(() => {
    cameraRef.current?.layers.disableAll()
    cameraRef.current?.layers.enable(RenderLayerID.Default)
    cameraRef.current?.layers.enable(RenderLayerID.VisibleCollidable)
    cameraRef.current?.layers.enable(RenderLayerID.VisibleNonCollidable)
  }, [])

  React.useEffect(() => {
    const controls = controlsRef.current
    if (controls) {
      const handleStart = () => {
        isUserInteracting.current = true
      }
      const handleEnd = () => {
        isUserInteracting.current = false
      }

      controls.addEventListener('start', handleStart)
      controls.addEventListener('end', handleEnd)

      return () => {
        controls.removeEventListener('start', handleStart)
        controls.removeEventListener('end', handleEnd)
      }
    }
  }, [])

  const boundingBox = React.useMemo(
    () => getBoundingBoxOfAssetModel(assetModel.data.components, show3D),
    [show3D, assetModel.data.components]
  )

  const { center, size } = React.useMemo(
    () => ({
      center: boundingBox.getCenter(new THREE.Vector3()),
      size: boundingBox.getSize(new THREE.Vector3()),
    }),
    [boundingBox]
  )

  // spread params so that interactions layer is not overwritten
  React.useEffect(
    () =>
      setController((prev) => ({
        ...prev,

        refitCameraToFocusedComponent: () => setCameraShow3D(!show3D),
      })),
    [setCameraShow3D, show3D, setController]
  )

  useFrame((_, delta) => {
    const lerpDelta = Math.min(1.0, 10 * delta)
    if (controlsRef.current && cameraRef.current) {
      const cameraDistance = calculateCameraDistance(
        size,
        cameraRef.current.fov,
        cameraRef.current.aspect
      )
      if (cameraShow3D !== show3D) {
        const distToFit =
          Math.max(boundingBox.max.x, boundingBox.max.y, boundingBox.max.z) +
          cameraDistance
        cameraRef.current.position.set(center.x, center.y, distToFit)
        controlsRef.current.target.copy(center)
        controlsRef.current.enablePan = true
        controlsRef.current.enableRotate = show3D

        controlsRef.current.mouseButtons = show3D
          ? {
              LEFT: THREE.MOUSE.ROTATE,
              MIDDLE: THREE.MOUSE.DOLLY,
              RIGHT: THREE.MOUSE.PAN,
            }
          : {
              LEFT: THREE.MOUSE.PAN,
              MIDDLE: THREE.MOUSE.ROTATE,
              RIGHT: THREE.MOUSE.DOLLY,
            }
      } else if (
        cameraShow3D &&
        autoRecenter &&
        !isUserInteracting.current &&
        controlsRef.current.target.distanceTo(center) > 0.001
      ) {
        controlsRef.current.target.lerp(center, lerpDelta)
      }
      const maxDistToFit =
        boundingBox.max.distanceTo(controlsRef.current.target) +
        cameraDistance * cameraFitMultiplier
      if (
        !isUserInteracting.current &&
        cameraRef.current.position.distanceTo(controlsRef.current.target) >
          maxDistToFit + 0.001
      ) {
        subVector.subVectors(
          cameraRef.current.position,
          controlsRef.current.target
        )
        subVector.normalize().multiplyScalar(maxDistToFit)
        cameraVector.addVectors(controlsRef.current.target, subVector)
        cameraRef.current.position.lerp(
          cameraVector,
          Math.min(1.0, 2.0 * delta)
        )
      }
      controlsRef.current.update()
    }
    setCameraShow3D((prevState) => (prevState !== show3D ? show3D : prevState))
  })

  //----------------------------------------------------------------------------

  return (
    <>
      <PerspectiveCamera makeDefault ref={cameraRef} fov={50} />
      <OrbitControls ref={controlsRef} />
      <MouseHandler />
    </>
  )
}
